import React, { useEffect, useMemo, useState } from 'react';

const ELIGIBLE_ROLES = ['driver', 'bus_company', 'guest_tour'];

const ROLE_LABELS = {
  driver: 'Driver',
  bus_company: 'Bus Company',
  guest_tour: 'Tour Organizer',
};

const STEP_MAP = {
  driver: [
    {
      title: 'Welcome to HopSeat',
      body: 'Your driver command center. The top-right shows your profile completion, points balance, and quick settings access.',
      selector: '[data-testid="driver-ops-settings-avatar-button"]',
    },
    {
      title: 'Find Driving Gigs',
      body: 'The Gigs tab is your most important destination — browse available tour gigs, apply, and track your applications all in one place.',
      selector: '[data-testid="gig-tab-available"]',
    },
    {
      title: 'Left Navigation',
      body: 'Use the left sidebar to move between Feed, Gigs, Service Reports, Messages, Profile, Network, and Settings.',
      selector: '[data-testid="driver-ops-sidebar"]',
    },
    {
      title: 'Road Resources',
      body: 'The Resources tab (paid membership) unlocks 212+ bus parking locations, 149 airports, 2,762 venues, bus mechanics, and company directories.',
      selector: '[data-testid="driver-ops-nav-resources"]',
    },
    {
      title: 'Profile & Points',
      body: 'Complete your driver profile and CDL documents. Earn HopSeat points for every gig completed — your tier determines your point multiplier.',
      selector: '[data-testid="driver-ops-nav-profile"]',
    },
  ],
  bus_company: [
    {
      title: 'Company Dashboard Entry',
      body: 'Start from your company command center for hiring and operations workflows.',
      selector: '[data-testid="company-dashboard-entry-shell-wrap"]',
    },
    {
      title: 'Post New Gig',
      body: 'Use this action to publish positions and start receiving driver applications.',
      selector: '[data-testid="post-gig-btn"]',
    },
    {
      title: 'Edit Profile',
      body: 'Keep company profile details current so drivers can trust and respond quickly.',
      selector: '[data-testid="edit-profile-btn"]',
    },
    {
      title: 'Plan & Billing',
      body: 'Manage plan tier and billing settings from the settings panel.',
      selector: '[data-testid="company-settings-plan-billing-panel"]',
    },
    {
      title: 'Workflow Navigation',
      body: 'Use dashboard navigation to switch between gigs, applications, profile, and settings.',
      selector: '.sidebar-nav',
    },
  ],
  guest_tour: [
    {
      title: 'Tour Dashboard Entry',
      body: 'Your organizer command center keeps tour staffing and operations aligned.',
      selector: '[data-testid="tour-dashboard-entry-shell-wrap"]',
    },
    {
      title: 'Create Opportunity',
      body: 'Post gigs for upcoming tour needs and monitor response activity.',
      selector: '[data-testid="post-gig-btn"]',
    },
    {
      title: 'Profile Updates',
      body: 'Maintain organizer profile details to improve trust and response quality.',
      selector: '[data-testid="edit-profile-btn"]',
    },
    {
      title: 'Plan & Billing',
      body: 'Review membership and billing settings from the organizer settings area.',
      selector: '[data-testid="tour-settings-plan-billing-panel"]',
    },
    {
      title: 'Navigation Basics',
      body: 'Use the side navigation for overview, gigs, applications, and account controls.',
      selector: '.sidebar-nav',
    },
  ],
};

const getStorageKey = (userId) => `hopseat_tutorial_state_${userId}`;

export const FirstLoginTutorialOverlay = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [neverShowAgain, setNeverShowAgain] = useState(false);
  const [completedOnce, setCompletedOnce] = useState(false);
  const [targetRect, setTargetRect] = useState(null);

  const role = user?.role;
  const steps = useMemo(() => STEP_MAP[role] || [], [role]);

  useEffect(() => {
    if (!user?.id || !ELIGIBLE_ROLES.includes(role)) {
      setIsOpen(false);
      return;
    }

    const storageKey = getStorageKey(user.id);
    const raw = localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : { completedOnce: false, neverShowAgain: false };

    setCompletedOnce(Boolean(parsed.completedOnce));
    setNeverShowAgain(Boolean(parsed.neverShowAgain));

    // Wave 3D: do NOT auto-open the full walkthrough. On genuine first login show
    // a small, dismissible welcome prompt; the full tour launches only on user
    // intent (or the Help/Settings "Replay Tutorial" action).
    if (!parsed.neverShowAgain && !parsed.completedOnce && steps.length > 0) {
      setStepIndex(0);
      setShowWelcome(true);
    }
  }, [user?.id, role, steps.length]);

  useEffect(() => {
    if (!user?.id || !ELIGIBLE_ROLES.includes(role)) return;

    const onReplay = (event) => {
      const eventUserId = event?.detail?.userId;
      if (eventUserId && eventUserId !== user.id) return;
      setShowWelcome(false);
      setStepIndex(0);
      setIsOpen(true);
    };

    window.addEventListener('hopseat:replay-tutorial', onReplay);
    return () => window.removeEventListener('hopseat:replay-tutorial', onReplay);
  }, [user?.id, role]);

  useEffect(() => {
    if (!isOpen || steps.length === 0) return;

    const updateRect = () => {
      const selector = steps[stepIndex]?.selector;
      if (!selector) {
        setTargetRect(null);
        return;
      }

      const el = document.querySelector(selector);
      if (!el) {
        setTargetRect(null);
        return;
      }

      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isOpen, stepIndex, steps]);

  // Keyboard dismiss: Esc closes, ArrowRight = Next, ArrowLeft = Back.
  // (Defined after handlers below via the listener effect.)

  // Wave 3D: small, non-blocking welcome prompt shown on genuine first login
  // instead of auto-opening the full walkthrough. Dismissing marks the tutorial
  // as seen so it never auto-appears again; the tour stays available from Settings.
  if (showWelcome && !isOpen && steps.length > 0) {
    const persistSeen = () => {
      if (user?.id) {
        localStorage.setItem(getStorageKey(user.id), JSON.stringify({ completedOnce: true, neverShowAgain }));
      }
      setCompletedOnce(true);
      setShowWelcome(false);
    };
    return (
      <div
        className="fixed inset-x-3 bottom-4 z-[110] pointer-events-auto sm:left-auto sm:right-4 sm:w-[360px]"
        data-testid="first-login-welcome-prompt"
      >
        <div className="rounded-2xl border border-orange-500/30 bg-[#111a2d] p-4 shadow-2xl">
          <div className="flex items-start justify-between gap-2">
            <div className="pr-6">
              <p className="text-[10px] uppercase tracking-[0.18em] text-orange-300">{ROLE_LABELS[role] || 'User'}</p>
              <h3 className="mt-1 text-base font-semibold text-white" data-testid="first-login-welcome-title">Welcome to HopSeat</h3>
            </div>
            <button
              type="button"
              onClick={persistSeen}
              aria-label="Dismiss welcome"
              className="flex h-8 w-8 items-center justify-center rounded-full text-slate-300 hover:bg-white/15 hover:text-white transition-colors"
              data-testid="first-login-welcome-dismiss"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" /></svg>
            </button>
          </div>
          <p className="mt-1 text-sm text-slate-200">
            Take a quick tour to learn your way around, or jump right in — you can start the tour anytime from Settings.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setShowWelcome(false); setStepIndex(0); setIsOpen(true); }}
              className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400"
              data-testid="first-login-welcome-take-tour"
            >
              Take a quick tour
            </button>
            <button
              type="button"
              onClick={persistSeen}
              className="rounded-lg px-3 py-1.5 text-xs text-slate-300 hover:bg-white/10 hover:text-white"
              data-testid="first-login-welcome-not-now"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen || steps.length === 0) return null;

  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const isMobile = window.innerWidth < 768;

  const saveState = (nextCompletedOnce, nextNeverShowAgain) => {
    if (!user?.id) return;
    localStorage.setItem(
      getStorageKey(user.id),
      JSON.stringify({
        completedOnce: nextCompletedOnce,
        neverShowAgain: nextNeverShowAgain,
      })
    );
  };

  // Close is ALWAYS available — no more "you must click through all 5 steps".
  // Closing marks the tutorial as completed-once so it won't auto-pop on next login;
  // user can replay anytime from settings via the hopseat:replay-tutorial event.
  const handleClose = () => {
    saveState(true, neverShowAgain);
    setCompletedOnce(true);
    setIsOpen(false);
  };

  // Skip = same as Close but explicit user intent ("Skip Tour" button)
  const handleSkip = () => handleClose();

  const handleNext = () => {
    if (!isLastStep) {
      setStepIndex((prev) => prev + 1);
      return;
    }
    saveState(true, neverShowAgain);
    setCompletedOnce(true);
    setIsOpen(false);
  };

  const handleBack = () => setStepIndex((prev) => Math.max(0, prev - 1));

  const cardStyle = (() => {
    if (isMobile || !targetRect) {
      return {
        left: 12,
        right: 12,
        bottom: 20,
      };
    }

    const width = 340;
    const rawLeft = Math.max(16, Math.min(targetRect.left, window.innerWidth - width - 16));
    const preferTop = targetRect.bottom + 14;
    const top = preferTop + 230 > window.innerHeight ? Math.max(16, targetRect.top - 238) : preferTop;

    return {
      width,
      left: rawLeft,
      top,
    };
  })();

  const spotlightStyle = targetRect
    ? {
        left: Math.max(0, targetRect.left - 6),
        top: Math.max(0, targetRect.top - 6),
        width: Math.max(0, targetRect.width + 12),
        height: Math.max(0, targetRect.height + 12),
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-[120] pointer-events-none"
      data-testid="first-login-tutorial-overlay"
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); handleClose(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); handleNext(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); handleBack(); }
      }}
      tabIndex={-1}
      ref={(el) => { if (el && isOpen) el.focus(); }}
    >
      {/* Backdrop — visual dimming ONLY. pointer-events-none so it never
          intercepts clicks on primary Home actions (e.g. Post a Gig). Dismiss
          via the X, Skip, Esc, or Finish. (Wave 3C non-blocking fix.) */}
      <div
        className="absolute inset-0 bg-black/60 pointer-events-none"
        data-testid="first-login-tutorial-backdrop"
        aria-hidden="true"
      />

      {spotlightStyle && (
        <div
          className="pointer-events-none absolute rounded-xl border-2 border-orange-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
          style={spotlightStyle}
          data-testid="first-login-tutorial-spotlight"
        />
      )}

      <div
        className="fixed rounded-2xl border border-white/15 bg-[#111a2d] p-4 text-white shadow-2xl pointer-events-auto"
        style={cardStyle}
        data-testid="first-login-tutorial-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="pr-8">
            <p className="text-[10px] uppercase tracking-[0.18em] text-orange-300" data-testid="first-login-tutorial-role-label">
              {ROLE_LABELS[role] || 'User'} Tutorial
            </p>
            <h3 className="mt-1 text-base font-semibold" data-testid="first-login-tutorial-step-title">{currentStep.title}</h3>
          </div>
          {/* Prominent X close — always enabled, top-right corner */}
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close tutorial"
            className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full text-slate-300 hover:bg-white/15 hover:text-white transition-colors"
            data-testid="first-login-tutorial-close-button"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-slate-200" data-testid="first-login-tutorial-step-description">{currentStep.body}</p>

        <div className="mt-3 rounded-lg border border-cyan-500/25 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-200" data-testid="first-login-tutorial-progress">
          Step {stepIndex + 1} of {steps.length} • Press Esc or close (✕) to dismiss
        </div>

        <label className="mt-3 flex items-center gap-2 text-xs text-slate-200" data-testid="first-login-tutorial-never-show-row">
          <input
            type="checkbox"
            checked={neverShowAgain}
            onChange={(e) => setNeverShowAgain(e.target.checked)}
            className="h-4 w-4 rounded border-slate-500"
            data-testid="first-login-tutorial-never-show-checkbox"
          />
          Never show this again
        </label>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleBack}
            disabled={stepIndex === 0}
            className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-slate-200 disabled:opacity-40 hover:bg-white/5"
            data-testid="first-login-tutorial-back-button"
          >
            Back
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSkip}
              className="rounded-lg px-3 py-1.5 text-xs text-slate-300 hover:bg-white/10 hover:text-white"
              data-testid="first-login-tutorial-skip-button"
            >
              Skip Tour
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400"
              data-testid="first-login-tutorial-next-button"
            >
              {isLastStep ? 'Finish Tutorial' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginTutorialOverlay;
